/**
 * src/lib/network/pollerBackoff.js — pure online-aware backoff for polling loops.
 *
 * TICKET-027 sub-A — past failure: ProviderStatusBanner polled
 * /api/data?action=provider-status every 60s with no offline check + no
 * consecutive-failure backoff. Single transient DNS outage produced ~250
 * cascaded console errors (CEO live test 2026-04-27).
 *
 * Pure function so the contract is testable without JSDOM, fake timers, or
 * supabase mocks. Caller is responsible for tracking `consecutiveFailures`
 * and `lastAttemptAt` in component state, plus listening to `navigator.onLine`
 * + `window.online`/`offline` events for the `online` flag.
 */

export const POLL_BASE_MS = 60_000; // 60s — same as the original interval
export const POLL_MAX_BACKOFF_MS = 10 * 60_000; // 10min — long enough not to hammer during outages

/**
 * Returns the recommended interval (ms) before the next poll attempt given
 * `consecutiveFailures`. Doubles per failure (2x, 4x, 8x, ...) capped at
 * `POLL_MAX_BACKOFF_MS`. After a success the caller resets failures to 0
 * and the next interval drops back to `POLL_BASE_MS`.
 */
export function nextBackoffMs(consecutiveFailures) {
  if (!consecutiveFailures || consecutiveFailures < 1) return POLL_BASE_MS;
  const doubled = POLL_BASE_MS * Math.pow(2, consecutiveFailures);
  return Math.min(doubled, POLL_MAX_BACKOFF_MS);
}

/**
 * Should the poller fire its fetch right now?
 *   - Hard-blocks when `online === false` (no point hitting the network).
 *   - Otherwise allows when `now - lastAttemptAt >= nextBackoffMs(consecutiveFailures)`.
 *   - First call (lastAttemptAt = 0) fires immediately when online.
 *
 * @param {{
 *   online: boolean,
 *   consecutiveFailures: number,
 *   lastAttemptAt: number,
 *   now: number,
 * }} state
 * @returns {boolean}
 */
export function shouldPoll({ online, consecutiveFailures, lastAttemptAt, now }) {
  if (!online) return false;
  const interval = nextBackoffMs(consecutiveFailures);
  return (now - lastAttemptAt) >= interval;
}
